import React, { useState, useEffect, useCallback } from "react";
import { Segment, Label, Statistic, Grid, Image, DropdownProps, Header, Form, Button, Divider, Progress } from "semantic-ui-react";

import TemplateMain from "../../components/TemplateMain";
import ReportsRepository, { ISummaryMonthResponse } from "../../Repositories/ReportsRepository";
import errors, { IAliancaErrors } from "../../Utils/ExtractErrors";
import RegionalManagerRepository, { IRegionalManagerListModel } from "../../Repositories/RegionalManagerRepository";
import SectorManagerRepository, { ISectorManagerListModel } from "../../Repositories/SectorManagerRepository";
// ICONS
import none from "../../assets/Icons/none.png";
import cristal from "../../assets/Icons/cristal.png";
import diamond from "../../assets/Icons/diamond.png";
import emerald from "../../assets/Icons/emerald.png";
import pearl from "../../assets/Icons/pearl.png";
import jade from "../../assets/Icons/jade.png";
import sapphire from "../../assets/Icons/sapphire.png";
import ruby from "../../assets/Icons/ruby.png";
import NumberFormat from "../../Utils/NumberFormat";
import { toast } from "react-toastify";

interface IState extends ISummaryMonthResponse {
    loading: boolean;
    year: number;
    month: number;
    regionalId: string;
    sectorId: string;
    apiErrors: IAliancaErrors;
}

const date = new Date();

const initialState: IState = {
    loading: false,
    acertos: 0,
    cadastros: 0,
    vendaMinima: 0,
    brutoVendido: 0,
    naoUsados: 0,
    totalPedidos: 0,
    debitosConferencia: 0,
    naoRetornou: 0,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    regionalId: "",
    sectorId: "",
    apiErrors: { errors: [] }
}

const SummaryMonthGs: React.FC = () => {

    const [state, setState] = useState<IState>(initialState);
    const [loading, setLoading] = useState<boolean>(false);
    const [regionalManagers, setRegionalManagers] = useState<IRegionalManagerListModel[]>([]);
    const [sectorManagers, setSectorManagers] = useState<ISectorManagerListModel[]>([]);

    const optionsMonth = [
        { key: "1", text: "JANEIRO", value: 1 },
        { key: "2", text: "FEVEREIRO", value: 2 },
        { key: "3", text: "MARÇO", value: 3 },
        { key: "4", text: "ABRIL", value: 4 },
        { key: "5", text: "MAIO", value: 5 },
        { key: "6", text: "JUNHO", value: 6 },
        { key: "7", text: "JULHO", value: 7 },
        { key: "8", text: "AGOSTO", value: 8 },
        { key: "9", text: "SETEMBRO", value: 9 },
        { key: "10", text: "OUTUBRO", value: 10 },
        { key: "11", text: "NOVEMBRO", value: 11 },
        { key: "12", text: "DEZEMBRO", value: 12 }
    ];

    const optionsYear = [
        { key: "1", text: "2020", value: 2020 },
        { key: "2", text: "2021", value: 2021 },
        { key: "3", text: "2022", value: 2022 },
        { key: "4", text: "2023", value: 2023 },
        { key: "5", text: "2024", value: 2024 },
        { key: "6", text: "2025", value: 2025 },
        { key: "7", text: "2026", value: 2026 },
    ];

    const load = async () => {

        setLoading(true);
        try {

            const response = await ReportsRepository.SummaryMonth({
                year: state.year,
                month: state.month,
                sectorId: state.sectorId,
                regionalId: state.regionalId
            });

            setState(state => ({
                ...state,
                acertos: response.acertos,
                cadastros: response.cadastros,
                vendaMinima: response.vendaMinima,
                brutoVendido: response.brutoVendido,
                naoUsados: response.naoUsados,
                totalPedidos: response.totalPedidos,
                debitosConferencia: response.debitosConferencia,
                naoRetornou: response.naoRetornou
            }));

        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    };

    const LoadRegionalManagers = useCallback(async () => {
        const result = await RegionalManagerRepository.List({
            filter: "Status == 1"
        });

        setRegionalManagers(result.list);
    }, []);

    useEffect(() => { LoadRegionalManagers() }, [LoadRegionalManagers]);

    const LoadSectorManagers = useCallback(async (id: string) => {
        const result = await SectorManagerRepository.List({
            filter: `GerenteRegionalId == "${id}" && Status == 1`
        });

        setSectorManagers(result.list);
    }, []);

    const onRegionalManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value, sectorId: "" }));
        LoadSectorManagers(value?.toString() || "");
    }

    const onDropdownChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    //POSITIVAS
    const cadastros = (state.cadastros / 40) * 25.00;
    const confEstimada = state.acertos - state.naoUsados;
    const vendaMin = (state.vendaMinima / confEstimada) * 37.50;

    const faturamentoEstimado = state.vendaMinima * 600;
    const faturamento = (state.brutoVendido / faturamentoEstimado) * 37.50;

    //NEGATIVAS  
    const semUso = (state.naoUsados / state.totalPedidos) * 100 * 0.2500;
    const devedoresEstimado = state.brutoVendido * 0.7;

    const kitsPerdidos = (state.naoRetornou / state.acertos) * 100 * 0.3750;

    const revsDevedores = state.debitosConferencia;
    const devedores = (revsDevedores / devedoresEstimado) * 100 * 0.3750;

    //DEMAIS
    const progressPositives = cadastros + vendaMin + faturamento;
    const progressNegatives = semUso + devedores + kitsPerdidos;

    const setIcon = () => {
        if (state.acertos <= 29) {
            return none;
        } else if (state.acertos <= 49) {
            return emerald;
        } else if (state.acertos <= 99) {
            return cristal;
        } else if (state.acertos <= 149) {
            return ruby;
        } else if (state.acertos <= 249) {
            return jade;
        } else if (state.acertos < 399) {
            return diamond;
        } else if (state.acertos < 599) {
            return sapphire;
        } else {
            return pearl;
        }
    }

    const setNivel = () => {
        if (state.acertos <= 29) {
            return "NÍVEL NÃO ALCANÇADO";
        } else if (state.acertos <= 49) {
            return "ESMERALDA";
        } else if (state.acertos <= 99) {
            return "CRISTAL";
        } else if (state.acertos <= 149) {
            return "RUBI";
        } else if (state.acertos <= 249) {
            return "JADE";
        } else if (state.acertos < 399) {
            return "DIAMANTE";
        } else if (state.acertos < 599) {
            return "SAFIRA";
        } else {
            return "PÉROLA";
        }
    };

    const setBaseSalary = () => {
        if (state.acertos <= 29) {
            return 0;
        } else if (state.acertos <= 49) {
            return 1500;
        } else if (state.acertos <= 99) {
            return 2500;
        } else if (state.acertos <= 149) {
            return 2900;
        } else if (state.acertos <= 249) {
            return 3700;
        } else if (state.acertos <= 399) {
            return 4500;
        } else if (state.acertos <= 599) {
            return 5300;
        } else {
            return 6100;
        }
    };

    const produtividade = () => {
        return progressPositives - progressNegatives;
    };

    const salary = () => {
        return (setBaseSalary() * produtividade()) / 100;
    };

    const colorSalary = () => {
        if (salary() <= 0) {
            return "red";
        }
        else {
            return "green";
        }
    }

    const content = (
        <>
            <Header icon="dollar" as="h1" content="Produtividade Gerente de Setor" dividing />
            <Segment>
                <Grid.Row>
                    <Grid.Column>
                        <Form>
                            <Form.Group widths="equal">
                                <Form.Field>
                                    <Form.Dropdown
                                        required
                                        fluid
                                        selection
                                        search
                                        scrolling
                                        name="month"
                                        placeholder="Selecione o mês"
                                        label="Mês"
                                        options={optionsMonth}
                                        value={state.month}
                                        onChange={onDropdownChange}

                                    />
                                </Form.Field>
                                <Form.Field>
                                    <Form.Dropdown
                                        required
                                        fluid
                                        selection
                                        search
                                        scrolling
                                        name="year"
                                        placeholder="Selecione o ano"
                                        label="Ano"
                                        options={optionsYear}
                                        value={state.year}
                                        onChange={onDropdownChange}
                                    />
                                </Form.Field>
                            </Form.Group>
                            <Form.Group widths="equal">
                                <Form.Field>
                                    <Form.Dropdown
                                        fluid
                                        selection
                                        search
                                        scrolling
                                        name="regionalId"
                                        noResultsMessage="Nenhum registro encontrado."
                                        placeholder="Selecione"
                                        label="G. Regional"
                                        onChange={onRegionalManagerChange}
                                        options={regionalManagers.map(regionalManager => { return { key: regionalManager.id, value: regionalManager.id, text: regionalManager.nome } })}
                                        value={state.regionalId}
                                        clearable
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <Form.Dropdown
                                        fluid
                                        selection
                                        search
                                        scrolling
                                        name="sectorId"
                                        noResultsMessage="Nenhum registro encontrado."
                                        placeholder="Selecione"
                                        label="G. Setor"
                                        onChange={onDropdownChange}
                                        options={sectorManagers.map(sectorManager => { return { key: sectorManager.id, value: sectorManager.id, text: sectorManager.nome } })}
                                        value={state.sectorId}
                                        clearable
                                    />
                                </Form.Field>
                            </Form.Group>
                        </Form>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column>
                        <Button
                            fluid
                            icon="search"
                            color="pink"
                            content="Pesquisar"
                            onClick={load}
                        />
                    </Grid.Column>
                </Grid.Row>
                <Segment placeholder textAlign="center">
                    <Label size="big" color="green" attached="top">
                        Produtividade
                  </Label>
                    <Grid textAlign="center" columns={2} verticalAlign="bottom">
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    <Image src={setIcon()} avatar />
                                </Statistic.Value>
                                <Statistic.Label>{setNivel()}</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    {NumberFormat.Currency(setBaseSalary())}
                                </Statistic.Value>
                                <Statistic.Label>BASE</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>{produtividade() > 0 ? produtividade().toFixed(2) : 0}%</Statistic.Value>
                                <Statistic.Label>PRODUTIVIDADE</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                        <Grid.Column>
                            <Statistic size="tiny" color={colorSalary()}>
                                <Statistic.Value>
                                    {NumberFormat.Currency(salary() > 0 ? salary() : 0)}
                                </Statistic.Value>
                                <Statistic.Label>MEU GANHO</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                    </Grid>
                </Segment>
                <Header
                    as="h3"
                    icon="plus circle"
                    color="teal"
                    content="CONDIÇÕES POSITIVAS"
                />
                <Divider />
                <Progress percent={progressPositives.toFixed(2)} progress indicating />
                <Grid columns={2}>
                    <Grid.Column>
                        <Segment textAlign="center">
                            <Label size="big" color="teal" attached="top">
                                NOVOS CADASTROS
                          </Label>
                            <Grid textAlign="center" columns={2}>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>40</Statistic.Value>
                                        <Statistic.Label>ESTIMADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>{state.cadastros}</Statistic.Value>
                                        <Statistic.Label>REALIZADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                    <Grid.Column>
                        <Segment textAlign="center">
                            <Label size="big" color="teal" attached="top">
                                CONFERÊNCIAS COM VENDA <small>(acima de R$ 50,00)</small>
                          </Label>
                            <Grid textAlign="center" columns={2}>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>{confEstimada}</Statistic.Value>
                                        <Statistic.Label>ESTIMADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>{state.vendaMinima}</Statistic.Value>
                                        <Statistic.Label>REALIZADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                </Grid>
                <Segment textAlign="center">
                    <Label size="big" color="teal" attached="top">
                        FATURAMENTO (BRUTO)
                  </Label>
                    <Grid textAlign="center" columns={2}>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    {NumberFormat.Currency(faturamentoEstimado)}
                                </Statistic.Value>
                                <Statistic.Label>ESTIMADO</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    {NumberFormat.Currency(state.brutoVendido)}
                                </Statistic.Value>
                                <Statistic.Label>REALIZADO</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                    </Grid>
                </Segment>

                <Header
                    as="h3"
                    icon="minus circle"
                    color="red"
                    content="CONDIÇÕES NEGATIVAS"
                />
                <Divider />
                <Progress percent={progressNegatives.toFixed(2)} color="red" progress />
                <Grid columns={2} >
                    <Grid.Column>
                        <Segment textAlign="center">
                            <Label size="big" color="red" attached="top">
                                KITS DEVOLVIDOS
                          </Label>
                            <Grid textAlign="center" columns={2}>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>{state.totalPedidos}</Statistic.Value>
                                        <Statistic.Label>ESTIMADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>{state.naoUsados}</Statistic.Value>
                                        <Statistic.Label>REALIZADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                    <Grid.Column>
                        <Segment textAlign="center">
                            <Label size="big" color="red" attached="top">
                                KITS PERDIDOS
                          </Label>
                            <Grid textAlign="center" columns={2}>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>
                                            {state.acertos}
                                        </Statistic.Value>
                                        <Statistic.Label>ESTIMADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                                <Grid.Column>
                                    <Statistic size="mini">
                                        <Statistic.Value>
                                            {state.naoRetornou}
                                        </Statistic.Value>
                                        <Statistic.Label>REALIZADO</Statistic.Label>
                                    </Statistic>
                                </Grid.Column>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                </Grid>
                <Segment textAlign="center">
                    <Label size="big" color="red" attached="top">
                        REVENDEDORES INADIMPLENTES
                          </Label>
                    <Grid textAlign="center" columns={2}>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    {NumberFormat.Currency(devedoresEstimado)}
                                </Statistic.Value>
                                <Statistic.Label>ESTIMADO</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                        <Grid.Column>
                            <Statistic size="mini">
                                <Statistic.Value>
                                    {NumberFormat.Currency(revsDevedores)}
                                </Statistic.Value>
                                <Statistic.Label>REALIZADO</Statistic.Label>
                            </Statistic>
                        </Grid.Column>
                    </Grid>
                </Segment>
            </Segment>
        </>
    )
    return (
        <TemplateMain
            loading={loading}
            content={content}
        />
    )
};

export default SummaryMonthGs;
